import { Component, Inject, OnInit, Optional } from '@angular/core';
import { Grid } from '@app/core/components/grid/grid.model';
import { GridOptions } from '@app/core/components/grid/grid.types';
import { ModalService } from '@app/core/services/modal.service';
import { TT_VIEW_NAVIGATOR, TTViewNavigator } from '@app/modal/state-modal/state-modal.component';
import { Base64UrlService } from '@app/services/base64url.service';
import { BspMenuGet, BspRecordRight, BspService } from '@app/services/bsp.service';

@Component({
    selector: 'tt-bsp-menu',
    templateUrl: './bsp-menu.component.html',
    styleUrls: ['./bsp-menu.component.css'],
    standalone: false,
})
export class BspMenuComponent implements OnInit {
    public params?: { bsp_keyno: string };

    public page?: BspMenuGet;

    public recordRights: BspRecordRight[] = [];

    public gridUsergroups?: GridOptions;

    public gridSubscriptions?: GridOptions;

    public gridSubscriptionSettings?: GridOptions;

    public gridTabs?: GridOptions;

    public gridDocuments?: GridOptions;

    public readonly bspNameButtons = [{
        id: 'goto',
        icon: 'fa fa-chevron-right',
        color: 'primary',
        type: 'primary',
        onClick: () => {
            this.navigator?.go({
                to: 'translateedit',
                parms: { item_id: this.page?.word_id }
            });
        }
    }];

    public copying = false;

    constructor(
        private bsp: BspService,
        private base64: Base64UrlService,
        private modal: ModalService,
        @Optional() @Inject(TT_VIEW_NAVIGATOR) private navigator?: TTViewNavigator,
    ) {}

    public async onTabChanged(): Promise<void> {
        if (!this.page) return;

        if (this.page.bg_bspmenu_main === 'TAB') this.recordRights = await this.bsp.listRecordRights();
    }

    public async copyChanges(): Promise<void> {
        if (!this.page) return;
        
        this.copying = true;
        
        try {
            const response = await this.bsp.copy(this.page);
            
            if (response.errorcode !== '0') {
                await this.modal.show({
                    type: 'warning',
                    title: 'error',
                    translateTitle: true,
                    message: response.errormessage
                });

                return;
            }
            
            await this.navigator?.back();
        } finally { this.copying = false; }
    }

    public applyRecordRight(): void {
        const recordRight = this.recordRights.find((item) => item.item_id === this.page?.recordright_no);
        
        for (const row of this.gridTabs?.gridfunc?.getIsSelectedRows(true) ?? []) {
            row['recordright_no'] = this.page?.recordright_no;
            row['recordright_name'] = recordRight?.item_name ?? '';
            
            this.gridTabs?.gridfunc?.updateRow(row);
        }
    }

    private createGrid(rememberId: string, method: number, key: string, keyno: string): Grid {
        return new Grid()
            .setRememberId(rememberId)
            .setLoadDataFunction({ method, parameters: { [key]: keyno } })
            .setKendoConfig({
                height: '100%',
                sortable: true,
                aggregate: true,
                pager: false,
                selectable: false,
                filterable: false
            })
            .setToolbar({
                wrapping: true,
                layouts: true
            })
            .setSpecialFunc({ newTab: true })
            .setCSSOptions({
                altColor: true,
                textWrapping: false
            });
    }

    private async loadPage(keyno: string): Promise<void> {
        this.page = await this.bsp.getMenu(keyno);
        
        this.page.html_description_base64 = this.base64.urlDecode(this.page.html_description_base64);
        
        this.navigator?.setHeading?.(this.page.bsp_name);
        
        this.gridUsergroups = this.createGrid('w_bspmenu_bspug_grid', 3162, 'bsp_keyno', this.page.bsp_keyno)
            .setCustomToolbarButtons([{
                name: 'bspmenu_bspug_new',
                text: 'bspmenu_bspug_new',
                icon: 'fa-plus',
                translate: true,
                cssClass: 'btn btn-primary im-grid-btn-xs-r',
                func: () => {
                    this.navigator?.go({
                        to: 'bspug',
                        parms: {
                            bsp_keyno: this.page?.bsp_keyno,
                            bspug_keyno: '0'
                        }
                    });
                }
            }]);
        
        this.gridSubscriptions = this.createGrid('w_bspmenu_bspugsub_grid', 3163, 'bsp_keyno', this.page.bsp_keyno)
            .setOptionFunc((event: any) => this.onSubscriptionCellClick(event));
        
        this.gridTabs = this.createGrid('w_bspmenu_bspugt_grid', 3165, 'bsp_keyno', this.page.bsp_keyno);
        
        this.gridDocuments = this.createGrid('w_bspmenu_bspsub_grid', 3166, 'bsp_keyno', this.page.bsp_keyno);
        
        if (this.page.bg_bspmenu_main === 'TAB') this.recordRights = await this.bsp.listRecordRights();
    }

    private onSubscriptionCellClick(event: any): void {
        if (!event || typeof event !== 'object') return;

        const eventData = (event as { data?: any }).data;
        
        if (!eventData || typeof eventData !== 'object') return;
        
        const clickedCell = (eventData as { clickedCell?: { dataItem?: Record<string, any> } }).clickedCell;
        
        const subscriptionKey = clickedCell?.dataItem?.['bspugsub_keyno'];
        
        if (typeof subscriptionKey === 'string') return;
        
        this.gridSubscriptionSettings = this.createGrid('w_bspmenu_bspugset_grid', 3164, 'bspugsub_keyno', subscriptionKey);
    }

    public async ngOnInit(): Promise<void> {
        const routeParams = this.navigator?.params as { bsp_keyno?: string } | undefined;
        const keyno = this.params?.bsp_keyno ?? routeParams?.bsp_keyno;
        
        if (keyno !== undefined) await this.loadPage(keyno);
    }
}
